import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Helper to convert Firestore timestamp or string date to ISO 8601 string.
 */
function toIsoString(timestamp) {
  if (!timestamp) return null;
  try {
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    if (typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000).toISOString();
    }
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d.toISOString();
  } catch {
    return null;
  }
}

/**
 * BlogSEO - Dynamic SEO metadata and Schema.org JSON-LD for Blog Detail Page (/blog/:slug).
 * Handles loading, not-found, and active article states cleanly.
 */
export default function BlogSEO({ post, slug, loading, error }) {
  // 1. Loading State
  if (loading) {
    return (
      <Helmet>
        <title>Loading Article... | DOR Dress On Rent</title>
      </Helmet>
    );
  }

  // 2. Error or Not Found State
  if (error || !post) {
    return (
      <Helmet>
        <title>Article Not Found | DOR Dress On Rent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  // 3. Dynamic Title
  const rawTitle = (post.metaTitle || post.title || 'Blog Post').trim();
  const pageTitle = /dor/i.test(rawTitle)
    ? rawTitle
    : `${rawTitle} | DOR Dress On Rent`;

  // 4. Dynamic Description (140-160 chars)
  let description = '';
  if (post.metaDescription && typeof post.metaDescription === 'string' && post.metaDescription.trim()) {
    description = post.metaDescription.trim();
  } else if (post.excerpt && typeof post.excerpt === 'string' && post.excerpt.trim()) {
    description = post.excerpt.trim();
  } else if (post.content && typeof post.content === 'string') {
    const plain = post.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    description = plain;
  } else {
    description = 'Read outfit styling tips, bridal lehenga guides, and fashion rental inspiration from DOR Dress On Rent.';
  }

  if (description.length > 160) {
    const truncated = description.slice(0, 157);
    const lastSpace = truncated.lastIndexOf(' ');
    description = (lastSpace > 120 ? truncated.slice(0, lastSpace) : truncated).trim() + '...';
  }

  // 5. Canonical URL
  const articleSlug = (post.slug || slug || '').trim();
  const canonicalUrl = `https://www.dordressonrent.com/blog/${encodeURIComponent(articleSlug)}`;

  // 6. Image
  const defaultImage = 'https://www.dordressonrent.com/og-image.jpg';
  const articleImage = (post.coverImage && typeof post.coverImage === 'string' && post.coverImage.trim())
    ? post.coverImage.trim()
    : defaultImage;

  // 7. Dates
  const datePublished = toIsoString(post.createdAt);
  const dateModified = toIsoString(post.updatedAt);

  // 8. Author
  let authorEntity = null;
  if (post.author && typeof post.author === 'string' && post.author.trim()) {
    const authorName = post.author.trim();
    const isOrg = /dor/i.test(authorName);
    authorEntity = {
      '@type': isOrg ? 'Organization' : 'Person',
      name: authorName,
    };
  }

  // 9. Schema.org BlogPosting Structured Data
  const blogPostingSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: (post.title || rawTitle).trim(),
    description: description,
    image: [articleImage],
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'DOR Dress On Rent',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.dordressonrent.com/logo192.png',
      },
    },
  };

  if (datePublished) {
    blogPostingSchema.datePublished = datePublished;
  }
  if (dateModified && dateModified !== datePublished) {
    blogPostingSchema.dateModified = dateModified;
  }
  if (authorEntity) {
    blogPostingSchema.author = authorEntity;
  }

  // 10. Schema.org BreadcrumbList Structured Data
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.dordressonrent.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://www.dordressonrent.com/blog',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: (post.title || rawTitle).trim(),
        item: canonicalUrl,
      },
    ],
  };

  return (
    <Helmet>
      {/* Title Tag */}
      <title>{pageTitle}</title>

      {/* Meta Description */}
      <meta name="description" content={description} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Meta Tags */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="DOR Dress On Rent" />
      <meta property="og:image" content={articleImage} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={articleImage} />

      {/* Structured Data: BlogPosting */}
      <script type="application/ld+json">
        {JSON.stringify(blogPostingSchema)}
      </script>

      {/* Structured Data: BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
}
